import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { InstanceSystem, type InstanceVariant } from './InstanceSystem';
import { createPropMaterial } from './PropMaterial';
import type { TerrainData } from './TerrainData';
import type { ColliderGrid } from '../player/Controller';

export interface OsmData {
  trails: { id: number; name: string; alt: string; kind: string; paved: boolean; width: number; informal: boolean; pts: [number, number][] }[];
  roads: { id: number; name: string; kind: string; width: number; pts: [number, number][] }[];
  barriers: { id: number; kind: string; material: string; pts: [number, number][] }[];
  areas: { id: number; kind: string; name: string; pts: [number, number][] }[];
  pois: { name: string; x: number; z: number; kind: string; ele: number | null }[];
}

interface Placement {
  prop: string;
  x: number;
  y: number;
  z: number;
  rot: number;
  sx: number;
  sy: number;
  sz: number;
  shear: number;
}

/** Signs placed at real trailheads / junctions (scene coordinates). */
const SIGNS: { x: number; z: number; face: number; lines: string[] }[] = [
  { x: -6.5, z: 9.0, face: 70, lines: ['NAVAJO LOOP TRAIL', 'Wall Street  ·  Two Bridges', '1.3 mi loop  ·  550 ft descent'] },
  { x: 18.5, z: 41.0, face: 10, lines: ['WALL STREET', 'Navajo Loop  →'] },
  { x: 350, z: 506, face: 230, lines: ["QUEEN'S GARDEN  ·  PEEKABOO", '← Navajo Loop to Sunset Pt  0.7 mi'] },
  { x: 288, z: -626, face: 120, lines: ["QUEEN'S GARDEN TRAIL", 'Navajo Loop  1.8 mi'] },
];

const PANELS: { x: number; z: number; face: number; title: string; body: string }[] = [
  { x: 2.0, z: 26.0, face: 90, title: 'Sunset Point', body: "Elevation 8,000 ft. Below: Thor's Hammer and the Silent City. Frost wedging — more than 200 freeze-thaw cycles a year — carves the Claron Formation into hoodoos." },
  { x: 279, z: -593, face: 100, title: 'Sunrise Point', body: "Queen's Garden lies below. The colors come from iron (reds, oranges, yellows) and manganese (purples) in the limestone." },
  { x: -262, z: 1022, face: 80, title: 'Inspiration Point', body: 'The Silent City: rows of hoodoos born from fins of rock, cut by frost and rain.' },
  { x: 747, z: 1893, face: 30, title: 'Bryce Point', body: 'The full sweep of the Bryce Amphitheater opens to the north.' },
];

function drawSign(lines: string[], w = 512, h = 170): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const g = c.getContext('2d')!;
  g.fillStyle = '#5b3a24';
  g.fillRect(0, 0, w, h);
  // wood grain
  for (let i = 0; i < 70; i++) {
    g.strokeStyle = `rgba(30,15,6,${0.05 + Math.random() * 0.08})`;
    g.beginPath();
    const y = Math.random() * h;
    g.moveTo(0, y);
    g.bezierCurveTo(w * 0.3, y + Math.random() * 6 - 3, w * 0.6, y + Math.random() * 6 - 3, w, y + Math.random() * 4 - 2);
    g.stroke();
  }
  g.fillStyle = '#efe4cf';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  const sizes = [38, 26, 24];
  lines.forEach((t, i) => {
    g.font = `600 ${sizes[i] ?? 22}px "Inter", system-ui, sans-serif`;
    g.fillText(t, w / 2, h * (lines.length === 2 ? [0.38, 0.72][i] : [0.27, 0.56, 0.8][i]), w - 30);
  });
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.flipY = false;
  t.anisotropy = 8;
  return t;
}

function drawPanel(title: string, body: string, w = 768, h = 512): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const g = c.getContext('2d')!;
  const grd = g.createLinearGradient(0, 0, 0, h);
  grd.addColorStop(0, '#d9693a');
  grd.addColorStop(0.55, '#f0b27a');
  grd.addColorStop(1, '#3b2a22');
  g.fillStyle = '#efe6d6';
  g.fillRect(0, 0, w, h);
  g.fillStyle = grd;
  g.fillRect(24, 24, w - 48, h * 0.48);
  // stylised hoodoo silhouettes
  g.fillStyle = 'rgba(80,35,20,0.55)';
  for (let i = 0; i < 26; i++) {
    const x = 30 + i * 28 + Math.random() * 10, bh = 40 + Math.random() * 120;
    g.fillRect(x, 24 + h * 0.48 - bh, 14 + Math.random() * 8, bh);
    g.beginPath(); g.arc(x + 9, 24 + h * 0.48 - bh, 11, 0, Math.PI * 2); g.fill();
  }
  g.fillStyle = '#2a1d16';
  g.font = '600 46px "Fraunces", Georgia, serif';
  g.fillText(title, 28, h * 0.6 + 14);
  g.font = '400 24px "Inter", system-ui, sans-serif';
  const words = body.split(' ');
  let line = '', y = h * 0.6 + 56;
  for (const wd of words) {
    if (g.measureText(line + wd).width > w - 60) { g.fillText(line, 28, y); line = ''; y += 31; }
    line += wd + ' ';
  }
  g.fillText(line, 28, y);
  g.font = '500 16px "Inter", system-ui, sans-serif';
  g.fillStyle = '#6b5446';
  g.fillText('National Park Service · U.S. Department of the Interior', 28, h - 22);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.flipY = false;
  t.anisotropy = 8;
  return t;
}

export class Props {
  readonly group = new THREE.Group();
  readonly systems: InstanceSystem[] = [];
  placements: Placement[] = [];

  async load(base: string, terrain: TerrainData, osm: OsmData, colliders: ColliderGrid, shadowUniforms: Record<string, THREE.IUniform>) {
    const gltf = await new GLTFLoader().loadAsync(`${base}models/props.glb`);
    gltf.scene.updateMatrixWorld(true);
    // prop -> lod -> material -> geometry
    const geo = new Map<string, Map<number, Map<string, THREE.BufferGeometry>>>();
    gltf.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      let n: THREE.Object3D | null = mesh;
      let lodName = '';
      while (n) { if (/_LOD\d$/.test(n.name)) { lodName = n.name; break; } n = n.parent; }
      if (!lodName) return;
      const prop = lodName.replace(/_LOD\d$/, '');
      const lod = +lodName.slice(-1);
      const mat = (mesh.material as THREE.Material).name || 'default';
      const g = mesh.geometry.clone();
      const m = new THREE.Matrix4().extractRotation(mesh.matrixWorld);
      m.scale(new THREE.Vector3().setFromMatrixScale(mesh.matrixWorld));
      g.applyMatrix4(m);
      g.computeBoundingSphere();
      if (!geo.has(prop)) geo.set(prop, new Map());
      const byLod = geo.get(prop)!;
      if (!byLod.has(lod)) byLod.set(lod, new Map());
      byLod.get(lod)!.set(mat, g);
    });

    this.placements = this.computePlacements(terrain, osm, colliders);

    // textures
    const il = new THREE.ImageLoader();
    const pendingTex: Promise<unknown>[] = [];
    const tex = (name: string, srgb: boolean) => {
      const t = new THREE.Texture();
      pendingTex.push(il.loadAsync(`${base}textures/props/${name}.webp`).then((img) => { t.image = img; t.needsUpdate = true; }));
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.flipY = false;
      t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      t.anisotropy = 8;
      return t;
    };
    const materials = ['stone_masonry', 'wood_weathered', 'wood_brown', 'steel_brown'];
    for (const matName of materials) {
      const variants: InstanceVariant[] = [];
      const propIndex = new Map<string, number>();
      for (const [prop, byLod] of geo) {
        const lods: THREE.BufferGeometry[] = [];
        for (let l = 0; l < 2; l++) { const g = byLod.get(l)?.get(matName); if (g) lods.push(g); }
        if (!lods.length) continue;
        propIndex.set(prop, variants.length);
        variants.push({ name: prop, lods, radius: lods[0].boundingSphere!.radius });
      }
      if (!variants.length) continue;
      const { material, depthMaterial } = createPropMaterial({
        map: tex(`${matName}_albedo`, true), normal: tex(`${matName}_normal`, false), rough: tex(`${matName}_rough`, false),
        metal: matName === 'steel_brown' ? 0.6 : 0,
      }, shadowUniforms);
      const sys = new InstanceSystem(variants, material, depthMaterial, { switchDist: [22], maxDist: 300, sizeRef: 1.6, shadowDist: 40 });
      sys.shadowMaxCascade = 0;
      const i0: number[] = [], i1: number[] = [], vv: number[] = [];
      for (const p of this.placements) {
        const vi = propIndex.get(p.prop);
        if (vi === undefined) continue;
        i0.push(p.x, p.y, p.z, p.rot); i1.push(p.sx, p.sy, p.sz, p.shear); vv.push(vi);
      }
      sys.setInstances(new Float32Array(i0), new Float32Array(i1), new Uint16Array(vv));
      this.systems.push(sys);
      this.group.add(sys.group);
    }
    await Promise.all(pendingTex);
    // sign / panel faces: unique canvas textures
    const faces: [string, string, THREE.CanvasTexture][] = [];
    SIGNS.forEach((s, i) => faces.push(['trail_sign', `sign${i}`, drawSign(s.lines)]));
    PANELS.forEach((p, i) => faces.push(['info_panel', `panel${i}`, drawPanel(p.title, p.body)]));
    for (const [prop, key, ctex] of faces) {
      const matName = prop === 'trail_sign' ? 'sign_face' : 'panel_face';
      const g0 = geo.get(prop)?.get(0)?.get(matName);
      const p = this.placements.find((q) => (q as Placement & { key?: string }).key === key);
      if (!g0 || !p) continue;
      const { material, depthMaterial } = createPropMaterial({ map: ctex }, shadowUniforms);
      const sys = new InstanceSystem([{ name: key, lods: [g0], radius: 2 }], material, depthMaterial, { switchDist: [], maxDist: 400, sizeRef: 1 });
      sys.setInstances(new Float32Array([p.x, p.y, p.z, p.rot]), new Float32Array([1, 1, 1, 0]), new Uint16Array([0]));
      this.systems.push(sys);
      this.group.add(sys.group);
    }
  }

  private computePlacements(terrain: TerrainData, osm: OsmData, colliders: ColliderGrid): Placement[] {
    const out: (Placement & { key?: string })[] = [];
    const H = (x: number, z: number) => terrain.heightAt(x, z);
    const near = terrain.near;
    const inNear = (x: number, z: number) => x > near.x0 + 20 && x < near.x0 + near.size - 20 && z > near.z0 + 20 && z < near.z0 + near.size - 20;
    // rot such that local +X points along (dx, dz): rotY maps +X to (cos a, 0, -sin a)
    const rotAlong = (dx: number, dz: number) => Math.atan2(-dz, dx);
    // rot such that local +Z (front) faces heading deg (0 = north, 90 = east)
    const rotFacing = (deg: number) => {
      const h = THREE.MathUtils.degToRad(deg);
      const fx = Math.sin(h), fz = -Math.cos(h);
      return Math.atan2(fx, fz); // +Z -> (sin a, 0, cos a)
    };
    const overlook = new THREE.Vector2(7, 22);

    for (const b of osm.barriers) {
      const pts = b.pts.filter(([x, z]) => inNear(x, z));
      if (pts.length < 2) continue;
      const nearOverlook = pts.some(([x, z]) => overlook.distanceTo(new THREE.Vector2(x, z)) < 45);
      if (b.kind === 'retaining_wall' || b.kind === 'wall') {
        for (let i = 0; i < pts.length - 1; i++) {
          const [x0, z0] = pts[i], [x1, z1] = pts[i + 1];
          const len = Math.hypot(x1 - x0, z1 - z0);
          if (len < 0.3) continue;
          const n = Math.max(1, Math.round(len / 2));
          const seg = len / n;
          for (let k = 0; k < n; k++) {
            const t0 = k / n, t1 = (k + 1) / n;
            const ax = x0 + (x1 - x0) * t0, az = z0 + (z1 - z0) * t0;
            const bx = x0 + (x1 - x0) * t1, bz = z0 + (z1 - z0) * t1;
            const ya = H(ax, az), yb = H(bx, bz);
            out.push({ prop: 'stone_wall_2m', x: ax, y: Math.min(ya, yb), z: az, rot: rotAlong(bx - ax, bz - az), sx: seg / 2, sy: 1, sz: 1, shear: (yb - ya) / seg });
            colliders.add({ x: (ax + bx) / 2, z: (az + bz) / 2, r: Math.max(0.35, seg / 2), y0: Math.min(ya, yb) - 1, y1: Math.max(ya, yb) + 0.9 });
          }
        }
        for (const [x, z] of [pts[0], pts[pts.length - 1]]) out.push({ prop: 'stone_wall_end', x, y: H(x, z), z, rot: 0, sx: 1, sy: 1, sz: 1, shear: 0 });
      } else if (b.kind === 'fence' || b.kind === 'guard_rail') {
        const metal = nearOverlook;
        const span = metal ? 2 : 3;
        const postProp = metal ? 'pipe_rail_post' : 'wood_fence_post';
        const railProp = metal ? 'pipe_rail_2m' : 'wood_fence_rail_3m';
        let prev: [number, number, number] | null = null;
        for (let i = 0; i < pts.length - 1; i++) {
          const [x0, z0] = pts[i], [x1, z1] = pts[i + 1];
          const len = Math.hypot(x1 - x0, z1 - z0);
          const n = Math.max(1, Math.round(len / span));
          for (let k = 0; k <= n; k++) {
            if (k === 0 && i > 0) continue;
            const x = x0 + (x1 - x0) * (k / n), z = z0 + (z1 - z0) * (k / n);
            const y = H(x, z);
            out.push({ prop: postProp, x, y, z, rot: Math.random() * 6.28, sx: 1, sy: 1, sz: 1, shear: 0 });
            if (prev) {
              const [px, py, pz] = prev;
              const L = Math.hypot(x - px, z - pz);
              out.push({ prop: railProp, x: px, y: py, z: pz, rot: rotAlong(x - px, z - pz), sx: L / span, sy: 1, sz: 1, shear: (y - py) / Math.max(L, 0.1) });
              colliders.add({ x: (x + px) / 2, z: (z + pz) / 2, r: Math.max(0.3, L / 2), y0: Math.min(y, py) - 0.5, y1: Math.max(y, py) + 1.1 });
            }
            prev = [x, y, z];
          }
        }
      }
    }
    // signs & panels & benches
    SIGNS.forEach((s, i) => out.push({ key: `sign${i}`, prop: 'trail_sign', x: s.x, y: H(s.x, s.z), z: s.z, rot: rotFacing(s.face), sx: 1, sy: 1, sz: 1, shear: 0 }));
    PANELS.forEach((p, i) => out.push({ key: `panel${i}`, prop: 'info_panel', x: p.x, y: H(p.x, p.z), z: p.z, rot: rotFacing(p.face + 180), sx: 1, sy: 1, sz: 1, shear: 0 }));
    for (const [x, z, face] of [[-14, 14, 90], [-18, 30, 100], [-9, -6, 80], [272, -600, 110], [-270, 1012, 90]] as [number, number, number][]) {
      out.push({ prop: 'bench', x, y: H(x, z), z, rot: rotFacing(face + 180), sx: 1, sy: 1, sz: 1, shear: 0 });
    }
    // trail furniture on steep official trails: water bars and timber steps
    for (const t of osm.trails) {
      if (t.informal || t.paved) continue;
      let acc = 0;
      for (let i = 0; i < t.pts.length - 1; i++) {
        const [x0, z0] = t.pts[i], [x1, z1] = t.pts[i + 1];
        if (!inNear(x0, z0)) continue;
        const len = Math.hypot(x1 - x0, z1 - z0);
        const grade = Math.abs(H(x1, z1) - H(x0, z0)) / Math.max(len, 0.1);
        acc += len;
        if (grade > 0.1 && acc > (grade > 0.22 ? 7 : 14)) {
          acc = 0;
          const x = (x0 + x1) / 2, z = (z0 + z1) / 2;
          const across = rotAlong(-(z1 - z0), x1 - x0);
          out.push({ prop: grade > 0.22 ? 'timber_step' : 'water_bar', x, y: H(x, z) + 0.02, z, rot: across, sx: Math.min(1.1, t.width / 1.3), sy: 1, sz: 1, shear: 0 });
        }
      }
    }
    return out;
  }
}
